import React, { useState, useEffect, useRef } from "react";
import { UTILS, INS } from "../config/API";
import { useNavigate } from 'react-router-dom';
import RadioField from "../components/RadioField";
import CheckboxField from "../components/CheckboxField";
import SignatureCanvas from "react-signature-canvas";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { fetchProfileData } from '../utils/profile';
import jsPDF from 'jspdf';
import ConfirmationModal from "../components/ConfirmationModal";
import autoTable from "jspdf-autotable";

const ThreeTaxReturn = ({ parseData, onItemSelect }) => {
    const navigate = useNavigate();
    const [formSections, setFormSections] = useState({
        taxrevenue: { fields: [], title: "Tax Revenue Information", signature: null, signatureText: "", fileUpload: null, fileUploadText: "", fileTypes: "" }
    });
    const [isLoading, setIsLoading] = useState(true);
    const inputRef = useRef(null);

    const [inputValue, setInputValue] = useState("");
    const [emailError, setEmailError] = useState("");
    const [isAPILoading, setIsAPILoading] = useState(false);
    const [formData, setFormData] = useState({});
    const [zipCodes, setZipCodes] = useState([]);
    const [showZipDropdown, setShowZipDropdown] = useState(false);
    const [options, setOptions] = useState([]);
    const [picklistCache, setPicklistCache] = useState({});
    const [signature, setSignature] = useState(null);
    const [files, setFiles] = useState([{ id: 1, file: null, type: "" }]);
    const [showSignature, setShowSignature] = useState(false);
    const [signatureText, setSignatureText] = useState("");
    const [showFileUpload, setShowFileUpload] = useState(false);
    const [fileUploadText, setFileUploadText] = useState("");
    const [isSigned, setIsSigned] = useState(false);
    const [date, setDate] = useState("");
    const [error, setError] = useState(null);
    const sigPad = useRef(null);
    const [fileTypes, setFileTypes] = useState("");
    const [profileData, setProfileData] = useState(null);
    const [taxTypeOptions, setTaxTypeOptions] = useState([]);
    const [retailersOptions, setRetailersOptions] = useState([]);

    const [exemptionCodeOptions, setExemptionCodeOptions] = useState([]);
    const [exemptionCodeFieldName, setExemptionCodeFieldName] = useState("");
    const [exemptionAmountFieldName, setExemptionAmountFieldName] = useState("");
    const [exemptionTaxTypeFieldName, setExemptionTaxTypeFieldName] = useState("");
    const [sectionGroups, setSectionGroups] = useState([{
        id: 1,
        exemptionGroups: [1],
        splitpayment: { fields: [], title: "Split Payment Information", signature: null, signatureText: "", fileUpload: null, fileUploadText: "", fileTypes: "" },
        exemption: { fields: [], title: "Exemption Information", signature: null, signatureText: "", fileUpload: null, fileUploadText: "", fileTypes: "" }
    }]);
    const [taxPayerName, setTaxPayerName] = useState("");

    const isEnroll = parseData?.activeComponent === "Enrollment Application";

    // State for tax calculations
    const [taxRates, setTaxRates] = useState({});
    const [splitPaymentFieldNames, setSplitPaymentFieldNames] = useState({
        'Gross Amount': '',
        'Reported Exempt Sales': '',
        'Payment for': '',
        'Actual Taxable Sales': '',
        'Actual Tax Amount Due': ''
    });

    // New state for confirmation
    const [showConfirmation, setShowConfirmation] = useState(false);

    // New state for download popup
    const [showDownloadPopup, setShowDownloadPopup] = useState(false);

    // New state to capture data before reset
    const [submittedData, setSubmittedData] = useState(null);

    useEffect(() => {
        const selectedTaxPayer = localStorage.getItem("selectedBusinessName");
        if (selectedTaxPayer) {
            setTaxPayerName(selectedTaxPayer);
        }
    }, []);

    // Update tax rates when taxTypeOptions change
    useEffect(() => {
        if (taxTypeOptions.length > 0) {
            const rates = {};
            taxTypeOptions.forEach(option => {
                if (option.dval === "Sales Tax") rates[option.sval] = 0.02;
                if (option.dval === "Tobacco Products Tax") rates[option.sval] = 0.07;
                if (option.dval === "Liquor Privilege Tax") rates[option.sval] = 0.03;
                if (option.dval === "Restaurant Privilege") rates[option.sval] = 0.01;
                if (option.dval === "Sales Tax (Excess of $2500 on Single Item Sale)") rates[option.sval] = 0.02;
                if (option.dval === "Transient Lodging Tax") rates[option.sval] = 0.10;
                if (option.dval === "Utility Business Activity Tax") rates[option.sval] = 0.10;
                // Add more Payment fors as needed
            });
            setTaxRates(rates);
        }
    }, [taxTypeOptions]);

    // Initialize field names when sectionGroups are set
    useEffect(() => {
        if (sectionGroups.length > 0 && sectionGroups[0].splitpayment.fields.length > 0) {
            const splitFields = sectionGroups[0].splitpayment.fields;
            const fieldNameMap = {};

            [
                'Gross Amount',
                'Reported Exempt Sales',
                'Payment for',
                'Actual Taxable Sales',
                'Actual Tax Amount Due'
            ].forEach(displayName => {
                const field = splitFields.find(f => f.displayname === displayName);
                if (field) fieldNameMap[displayName] = field.fieldname;
            });

            setSplitPaymentFieldNames(fieldNameMap);
        }
    }, [sectionGroups]);

    // Handle tax calculations when relevant fields change
    useEffect(() => {
        sectionGroups.forEach(group => {
            const taxTypeKey = `${splitPaymentFieldNames['Payment for']}_${group.id}`;
            const grossAmountKey = `${splitPaymentFieldNames['Gross Amount']}_${group.id}`;
            const exemptSalesKey = `${splitPaymentFieldNames['Reported Exempt Sales']}_${group.id}`;
            const taxableSalesKey = `${splitPaymentFieldNames['Actual Taxable Sales']}_${group.id}`;
            const taxDueKey = `${splitPaymentFieldNames['Actual Tax Amount Due']}_${group.id}`;

            const taxType = formData[taxTypeKey];
            const grossAmount = parseFloat(formData[grossAmountKey] || 0);
            const exemptSales = parseFloat(formData[exemptSalesKey] || 0);

            // Only calculate if we have all necessary values
            if (taxType && !isNaN(grossAmount)) {
                const taxRate = taxRates[taxType] || 0;
                const taxableSales = grossAmount - exemptSales;
                const taxDue = taxableSales * taxRate;

                setFormData(prev => ({
                    ...prev,
                    [taxableSalesKey]: taxableSales.toFixed(2),
                    [taxDueKey]: taxDue.toFixed(2)
                }));
            }
        });
    }, [
        formData,
        sectionGroups,
        splitPaymentFieldNames,
        taxRates
    ]);

    // Add this function near the top of your component, after the state declarations
    const getSQLDefaultValue = (defaultValue) => {
        if (defaultValue === "SQL_value SELECT MONTH(GETDATE()) - 1") {
            // Get last month (0-11, so we need to adjust)
            const lastMonth = new Date().getMonth(); // Current month (0-11)
            return lastMonth === 0 ? 12 : lastMonth; // If January (0), return December (12)
        }

        if (defaultValue === "SQL_value SELECT YEAR(GETDATE())") {
            // Get current year
            return new Date().getFullYear();
        }
        if (defaultValue === "sysdate") {
            // Return current date in dd/mm/yyyy format
            const date = new Date();
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        }

        return defaultValue;
    };

    useEffect(() => {
        const fetchFormFields = async (itype, stype) => {
            setIsLoading(true);
            try {
                const response = await fetch(UTILS, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                        "Accept": "application/json"
                    },
                    body: new URLSearchParams({
                        action: "desc",
                        itype: itype,
                        stype: stype,
                    }),
                });

                const text = await response.text();
                const cleanedText = text.replace(/[\x00-\x1F\x7F-\x9F]/g, "");
                console.log(cleanedText);

                let data;
                try {
                    data = JSON.parse(cleanedText);

                } catch (parseError) {
                    console.error("Failed to parse form fields:", parseError);
                    throw new Error("Invalid server response format");
                }

                if (response.ok) {
                    return data;
                } else {
                    throw new Error(data.message || "Failed to fetch form fields");
                }
            } catch (error) {
                console.error("Error loading form:", error);
                setError(error.message);
                return null;
            }
        };

        const fetchDropDownFields = async (itemValueGroup) => {
            if (picklistCache[itemValueGroup]?.data) return;

            setPicklistCache((prev) => ({
                ...prev,
                [itemValueGroup]: { ...prev[itemValueGroup], isLoading: true },
            }));

            try {
                const response = await fetch(UTILS, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                        "Accept": "application/json"
                    },
                    body: new URLSearchParams({ action: "idv", s: itemValueGroup || "YesNo" }),
                });

                const text = await response.text();
                const cleanedText = text.replace(/[\x00-\x1F\x7F-\x9F]/g, "");
                let data;
                try {
                    data = JSON.parse(cleanedText);
                } catch (parseError) {
                    console.error("Failed to parse dropdown data:", parseError);
                    console.error("Original text:", text);
                    throw new Error("Invalid dropdown data format");
                }

                setPicklistCache((prev) => ({
                    ...prev,
                    [itemValueGroup]: { data, isLoading: false },
                }));
            } catch (error) {
                console.error("Error fetching dropdown:", error);
                setPicklistCache((prev) => ({
                    ...prev,
                    [itemValueGroup]: { isLoading: false, error: error.message },
                }));
            }
        };

        const fetchAndCombineData = async () => {
            try {

                var firsttype = "clin"
                var firststype = "otherstampinv"
                var secondtype = "clin"
                var secondstype = "otherstampinv"
                if (parseData.sType === 'packsInv') {
                    firsttype = "clin"
                    firststype = "stampsinv"
                    secondtype = "clin"
                    secondstype = "packsdistributed"
                } else if (parseData.sType === 'taxpayments') {
                    firsttype = "funding"
                    firststype = "splitpayment"
                    secondtype = "funding"
                    secondstype = "exemption"
                } else if (parseData.sType === 'casino') {
                    firsttype = "clin"
                    firststype = "casino"
                    secondtype = "part"
                    secondstype = "casinooutlet"
                }


                const taxRevenueData = await fetchFormFields("funding", "taxrevenue");
                const splitPaymentData = await fetchFormFields(firsttype, firststype);
                const fundingExcemption = await fetchFormFields(secondtype, secondstype);

                if (!taxRevenueData || !splitPaymentData || !fundingExcemption) return;

                // Store exemption code field name
                const exemptionCodeField = fundingExcemption.Fields?.find(f => f.displayname === "Exemption Code");
                if (exemptionCodeField) {
                    setExemptionCodeFieldName(exemptionCodeField.fieldname);
                }

                // Store exemption amount field name
                const exemptionAmountField = fundingExcemption.Fields?.find(f => f.displayname === "Exemption Amount");
                if (exemptionAmountField) {
                    setExemptionAmountFieldName(exemptionAmountField.fieldname);
                }

                // Store Payment for field name for exemption section
                const exemptionTaxTypeField = fundingExcemption.Fields?.find(f => f.displayname === "Payment for");
                if (exemptionTaxTypeField) {
                    setExemptionTaxTypeFieldName(exemptionTaxTypeField.fieldname);
                }

                setFormSections(prev => ({
                    taxrevenue: {
                        ...prev.taxrevenue,
                        fields: taxRevenueData.Fields || [],
                        signature: taxRevenueData.Signature,
                        signatureText: taxRevenueData.SignatureText || "",
                        fileUpload: taxRevenueData.FileUpload,
                        fileUploadText: taxRevenueData.FileUploadText || "",
                        fileTypes: taxRevenueData.FileTypes || ""
                    }
                }));

                setSectionGroups([{
                    id: 1,
                    exemptionGroups: [1],
                    splitpayment: {
                        fields: splitPaymentData.Fields || [],
                        signature: splitPaymentData.Signature,
                        signatureText: splitPaymentData.SignatureText || "",
                        fileUpload: splitPaymentData.FileUpload,
                        fileUploadText: splitPaymentData.FileUploadText || "",
                        fileTypes: splitPaymentData.FileTypes || ""
                    },
                    exemption: {
                        fields: fundingExcemption.Fields || [],
                        signature: fundingExcemption.Signature,
                        signatureText: fundingExcemption.SignatureText || "",
                        fileUpload: fundingExcemption.FileUpload,
                        fileUploadText: fundingExcemption.FileUploadText || "",
                        fileTypes: fundingExcemption.FileTypes || ""
                    }
                }]);

                const allFields = [
                    ...(taxRevenueData.Fields || []),
                    ...(splitPaymentData.Fields || []),
                    ...(fundingExcemption.Fields || [])
                ];

                const initialFormData = {};
                allFields.forEach((field) => {
                    if (field.defaultvalue) {
                        initialFormData[field.fieldname] = getSQLDefaultValue(field.defaultvalue);
                    }
                });

                // Initialize group-specific fields for the first section group
                if (exemptionCodeFieldName) {
                    initialFormData[`${exemptionCodeFieldName}_1_1`] = "";
                }
                if (exemptionAmountFieldName) {
                    initialFormData[`${exemptionAmountFieldName}_1_1`] = "";
                }
                if (exemptionTaxTypeFieldName) {
                    initialFormData[`${exemptionTaxTypeFieldName}_1_1`] = "";
                }

                setFormData(initialFormData);

                const hasSignature = taxRevenueData.Signature || splitPaymentData.Signature || fundingExcemption.Signature;
                const hasFileUpload = taxRevenueData.FileUpload || splitPaymentData.FileUpload || fundingExcemption.FileUpload;

                setShowSignature(hasSignature);
                setShowFileUpload(hasFileUpload);

                const picklistFields = allFields.filter(
                    (field) => field.fieldtype === "picklist"
                );
                await Promise.all(
                    picklistFields.map(async (field) => {
                        await fetchDropDownFields(field.itemvaluegroup);
                    })
                );
            } catch (error) {
                console.error("Error in fetchAndCombineData:", error);
                setError(error.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAndCombineData();
    }, []);

    useEffect(() => {
        const fetchTaxTypes = async () => {
            try {
                const response = await fetch(UTILS, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                        "Accept": "application/json"
                    },
                    body: new URLSearchParams({
                        action: "sqlo",
                        itype: "funding",
                        stype: "splitpayment",
                        fldn: "flexfield31"
                    }),
                });

                const text = await response.text();
                const cleanedText = text.replace(/[\x00-\x1F\x7F-\x9F]/g, "");

                let data;
                try {
                    data = JSON.parse(cleanedText);
                } catch (parseError) {
                    console.error("Failed to parse Payment for data:", parseError);
                    throw new Error("Invalid Payment for data format");
                }

                if (response.ok) {
                    setTaxTypeOptions(data);
                } else {
                    throw new Error(data.message || "Failed to fetch Payment fors");
                }
            } catch (error) {
                console.error("Error fetching Payment fors:", error);
            }
        };
        fetchTaxTypes();

    }, []);


    useEffect(() => {
        const fetchRetailers = async () => {
            try {
                const response = await fetch(UTILS, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                        "Accept": "application/json"
                    },
                    body: new URLSearchParams({
                        action: "getretailers",

                    }),
                });

                const text = await response.text();
                const cleanedText = text.replace(/[\x00-\x1F\x7F-\x9F]/g, "");

                let data;
                try {
                    data = JSON.parse(cleanedText);
                } catch (parseError) {
                    console.error("Failed to parse Payment for data:", parseError);
                    throw new Error("Invalid Payment for data format");
                }

                if (response.ok) {
                    setRetailersOptions(Array.isArray(data?.data) ? data.data : []);

                } else {
                    throw new Error(data.message || "Failed to fetch Payment fors");
                }
            } catch (error) {
                console.error("Error fetching Payment fors:", error);
            }
        };
        fetchRetailers();

    }, []);

    const fetchExemptionCodes = async (taxType, groupId, exemptionId) => {
        try {
            const response = await fetch(UTILS, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "Accept": "application/json"
                },
                body: new URLSearchParams({
                    action: "exempcds",
                    itype: "funding",
                    stype: "exemption",
                    fldn: "flexfield25",
                    hiddendefaults: taxType
                }),
            });

            const text = await response.text();
            const cleanedText = text.replace(/[\x00-\x1F\x7F-\x9F]/g, "");
            console.log(cleanedText)

            let data;
            try {
                data = JSON.parse(cleanedText);
            } catch (parseError) {
                console.error("Failed to parse exemption code data:", parseError);
                throw new Error("Invalid exemption code data format");
            }

            if (response.ok) {
                // Update state to store exemption codes by group and exemption ID
                setExemptionCodeOptions(prev => ({
                    ...prev,
                    [`${groupId}_${exemptionId}`]: data
                }));
            } else {
                throw new Error(data.message || "Failed to fetch exemption codes");
            }
        } catch (error) {
            console.error("Error fetching exemption codes:", error);
        }
    };

    const handleAddExemptionGroup = (groupId) => {
        setSectionGroups(prevGroups => {
            return prevGroups.map(group => {
                if (group.id === groupId) {
                    const newExemptionId = group.exemptionGroups.length > 0
                        ? Math.max(...group.exemptionGroups) + 1
                        : 1;

                    return {
                        ...group,
                        exemptionGroups: [...group.exemptionGroups, newExemptionId]
                    };
                }
                return group;
            });
        });

        setFormData(prev => {
            const group = sectionGroups.find(g => g.id === groupId);
            const newExemptionId = group && group.exemptionGroups.length > 0
                ? Math.max(...group.exemptionGroups) + 1
                : 1;

            const newData = { ...prev };
            if (exemptionCodeFieldName) {
                newData[`${exemptionCodeFieldName}_${groupId}_${newExemptionId}`] = "";
            }
            if (exemptionAmountFieldName) {
                newData[`${exemptionAmountFieldName}_${groupId}_${newExemptionId}`] = "";
            }
            if (exemptionTaxTypeFieldName) {
                newData[`${exemptionTaxTypeFieldName}_${groupId}_${newExemptionId}`] = "";
            }
            return newData;
        });
    };

    const handleRemoveExemptionGroup = (groupId, exemptionId) => {
        setSectionGroups(prevGroups => {
            return prevGroups.map(group => {
                if (group.id === groupId) {
                    if (group.exemptionGroups.length > 1) {
                        return {
                            ...group,
                            exemptionGroups: group.exemptionGroups.filter(id => id !== exemptionId)
                        };
                    }
                }
                return group;
            });
        });

        setFormData(prev => {
            const newData = { ...prev };
            if (exemptionCodeFieldName) {
                delete newData[`${exemptionCodeFieldName}_${groupId}_${exemptionId}`];
            }
            if (exemptionAmountFieldName) {
                delete newData[`${exemptionAmountFieldName}_${groupId}_${exemptionId}`];
            }
            if (exemptionTaxTypeFieldName) {
                delete newData[`${exemptionTaxTypeFieldName}_${groupId}_${exemptionId}`];
            }
            return newData;
        });
    };

    const handleAddSectionGroup = () => {
        const newGroupId = sectionGroups.length > 0
            ? Math.max(...sectionGroups.map(g => g.id)) + 1
            : 1;

        setSectionGroups(prev => [
            ...prev,
            {
                id: newGroupId,
                exemptionGroups: [1],
                splitpayment: { ...prev[0].splitpayment },
                exemption: { ...prev[0].exemption }
            }
        ]);

        // Initialize form data for the new group
        const initialData = {};
        [...sectionGroups[0].splitpayment.fields, ...sectionGroups[0].exemption.fields].forEach(field => {
            if (field.defaultvalue) {
                initialData[`${field.fieldname}_${newGroupId}`] = getSQLDefaultValue(field.defaultvalue);
            }
        });

        setFormData(prev => ({
            ...prev,
            ...initialData,
            ...(exemptionCodeFieldName && { [`${exemptionCodeFieldName}_${newGroupId}_1`]: "" }),
            ...(exemptionAmountFieldName && { [`${exemptionAmountFieldName}_${newGroupId}_1`]: "" }),
            ...(exemptionTaxTypeFieldName && { [`${exemptionTaxTypeFieldName}_${newGroupId}_1`]: "" })
        }));
    };

    const handleRemoveSectionGroup = (groupId) => {
        if (sectionGroups.length > 1) {
            setSectionGroups(prev => prev.filter(group => group.id !== groupId));

            // Remove all fields related to this group
            setFormData(prev => {
                const newData = { ...prev };
                Object.keys(prev).forEach(key => {
                    if (key.endsWith(`_${groupId}`) ||
                        key.startsWith(`${exemptionCodeFieldName}_${groupId}_`) ||
                        key.startsWith(`${exemptionAmountFieldName}_${groupId}_`) ||
                        key.startsWith(`${exemptionTaxTypeFieldName}_${groupId}_`)) {
                        delete newData[key];
                    }
                });
                return newData;
            });
        }
    };

    const handleHomeClick = () => {
        navigate("/home");
        if (onItemSelect) onItemSelect();
    };

    const prepareRequestData = () => {
        const requestData = {
            uid: "123213",
            taxInfo: {},
            splitPayment: []
        };

        // 1. Add tax revenue information
        formSections.taxrevenue.fields.forEach(field => {
            if (formData[field.fieldname]) {
                requestData.taxInfo[field.fieldname] = formData[field.fieldname];
            }
        });
        const taxPayer = localStorage.getItem("selectedTaxPayer");
        requestData.taxInfo["flexfield17"] = taxPayer;

        // 2. Add split payment and exemption information
        sectionGroups.forEach(group => {
            const splitPaymentGroup = {
                exemptions: []
            };

            // Add split payment fields
            group.splitpayment.fields.forEach(field => {
                const fieldKey = `${field.fieldname}_${group.id}`;
                if (formData[fieldKey]) {
                    splitPaymentGroup[field.fieldname] = formData[fieldKey];
                }
            });

            // Add exemption groups
            group.exemptionGroups.forEach(exemptionId => {
                const exemptionGroup = {};

                group.exemption.fields.forEach(field => {
                    const fieldKey = `${field.fieldname}_${group.id}_${exemptionId}`;
                    const fieldValue = formData[fieldKey];

                    // Handle empty exemption amount - send 0 instead of empty string
                    if (field.displayname === "Exemption Amount") {
                        exemptionGroup[field.fieldname] = fieldValue === "" || fieldValue === null || fieldValue === undefined ? "0" : fieldValue;
                    } else if (fieldValue) {
                        exemptionGroup[field.fieldname] = fieldValue;
                    }
                });

                if (Object.keys(exemptionGroup).length > 0) {
                    splitPaymentGroup.exemptions.push(exemptionGroup);
                }
            });

            if (taxPayer) {

                if (parseData.sType === 'packsInv') {
                    splitPaymentGroup.acceptance = taxPayer;
                } else if (parseData.sType === 'taxpayments') {
                    splitPaymentGroup.flexfield1 = taxPayer;
                }

            }



            if (Object.keys(splitPaymentGroup).length > 1 || splitPaymentGroup.exemptions.length > 0) {
                requestData.splitPayment.push(splitPaymentGroup);
            }
        });

        return requestData;
    };

    // Updated generatePDF to accept parameters
    const generatePDF = (pdfFormData, pdfSectionGroups, pdfTaxPayerName) => {
        const doc = new jsPDF({ unit: "pt", format: "a4" });

        doc.setProperties({
            title: "Tax Return Summary",
            subject: "Tax Return PDF",
            creator: "Tax Portal",
        });

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const brand = { r: 113, g: 21, b: 21 }; // #711515
        const topMargin = 110;
        const bottomMargin = 60;
        const sideMargin = 30;

        const drawHeader = (pageNumber) => {
            doc.setFillColor(brand.r, brand.g, brand.b);
            doc.rect(0, 0, pageWidth, 70, "F");

            doc.setTextColor(0, 0, 0);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(16);
            doc.text("Tax Return Summary", sideMargin, 30);

            doc.setFont("helvetica", "normal");
            doc.setFontSize(11);
            const now = new Date().toLocaleString();
            doc.text(`Generated: ${now}`, sideMargin, 50);

            const forText = `For: ${pdfTaxPayerName || "N/A"}`;
            const forWidth = doc.getTextWidth(forText);
            doc.text(forText, pageWidth - sideMargin - forWidth, 50);
        };

        const drawFooter = (pageNumber, totalPages) => {
            doc.setFillColor(245, 247, 250);
            doc.rect(0, pageHeight - 50, pageWidth, 50, "F");

            doc.setTextColor(0, 0, 0);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);

            const refText = `Ref: ${String(new Date().getTime()).slice(-8)}`;
            doc.text(refText, sideMargin, pageHeight - 20);

            const pageText = `Page ${pageNumber} of ${totalPages}`;
            const pageWidthText = doc.getTextWidth(pageText);
            doc.text(pageText, pageWidth - sideMargin - pageWidthText, pageHeight - 20);
        };

        const tableOpts = {
            margin: {
                top: topMargin,
                bottom: bottomMargin,
                left: sideMargin,
                right: sideMargin,
            },
            styles: {
                font: "helvetica",
                fontSize: 10,
                cellPadding: 6,
                textColor: 33,
                lineWidth: 0.1,
                lineColor: [226, 232, 240],
            },
            headStyles: {
                fillColor: [brand.r, brand.g, brand.b],
                textColor: 255,
                fontStyle: "bold",
                halign: "left",
                valign: "middle",
            },
            alternateRowStyles: {
                fillColor: [249, 250, 251],
            },
            bodyStyles: {
                valign: "top",
            },
            theme: "striped",
        };

        drawHeader(1);

        const taxRevenueRows = (formSections?.taxrevenue?.fields || []).map(
            (field) => {
                const label = field.displayname || "";
                const value = pdfFormData?.[field.fieldname] ?? "N/A";
                return [label, String(value)];
            }
        );

        autoTable(doc, {
            ...tableOpts,
            startY: topMargin + 12,
            head: [["Field", "Value"]],
            body: taxRevenueRows,
        });

        let cursorY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 20 : topMargin + 20;

        const ensureSpace = (needed = 120) => {
            if (cursorY > pageHeight - bottomMargin - needed) {
                doc.addPage();
                drawHeader(doc.getNumberOfPages());
                cursorY = topMargin + 10;
            }
        };

        (pdfSectionGroups || []).forEach((group) => {
            ensureSpace(140);
            doc.setDrawColor(229, 231, 235);
            doc.setFillColor(241, 245, 249);
            doc.roundedRect(
                sideMargin,
                cursorY,
                pageWidth - sideMargin * 2,
                28,
                6,
                6,
                "F"
            );

            doc.setTextColor(0, 0, 0);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(12);
            doc.text(`Line ${group.id}`, sideMargin + 10, cursorY + 18);
            cursorY += 42;

            const splitRows = (group?.splitpayment?.fields || []).map((f) => {
                const label = f.displayname || "";
                const v = pdfFormData?.[`${f.fieldname}_${group.id}`] ?? "N/A";
                return [label, String(v)];
            });

            autoTable(doc, {
                ...tableOpts,
                startY: cursorY,
                head: [["Split Payment", "Value"]],
                body: splitRows,
            });
            cursorY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 16 : cursorY + 16;

            const exGroups = Array.isArray(group?.exemptionGroups)
                ? group.exemptionGroups
                : [];
            const exFields = group?.exemption?.fields || [];

            exGroups.forEach((exemptionId) => {
                ensureSpace(120);
                doc.setDrawColor(219, 234, 254);
                doc.setFillColor(239, 246, 255);
                doc.roundedRect(
                    sideMargin,
                    cursorY,
                    pageWidth - sideMargin * 2,
                    24,
                    5,
                    5,
                    "F"
                );

                doc.setTextColor(0, 0, 0);
                doc.setFont("helvetica", "bold");
                doc.setFontSize(11);
                doc.text(
                    parseData?.sType === 'packsInv'
                        ? `Packs Distributed ${exemptionId}`
                        : parseData?.sType === 'casino'
                            ? `Outlets ${exemptionId}`
                            : `Exemption ${exemptionId}`,
                    sideMargin + 10,
                    cursorY + 16
                );
                cursorY += 34;

                const exRows = exFields.map((f) => {
                    const label = f.displayname || "";
                    const v =
                        pdfFormData?.[`${f.fieldname}_${group.id}_${exemptionId}`] ?? "N/A";
                    return [label, String(v)];
                });

                autoTable(doc, {
                    ...tableOpts,
                    startY: cursorY,
                    head: [["Exemption Field", "Value"]],
                    body: exRows,
                });
                cursorY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 16 : cursorY + 16;
            });
        });

        const totalPages = doc.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            drawHeader(i);
            drawFooter(i, totalPages);
        }

        doc.setDisplayMode("fullwidth");

        doc.save("tax-return-form.pdf");
    };

    // Updated confirmSubmit (capture data before reset)
    const confirmSubmit = async () => {
        setShowConfirmation(false);
        setIsAPILoading(true);
        setError(null);

        const requestData = prepareRequestData();

        // Debug: Log the prepared data
        console.log("Prepared request data:", JSON.stringify(requestData, null, 2));

        var endpoint = ""


        if (parseData.sType === 'packsInv') {
            endpoint = "packsinv"
        } else if (parseData.sType === 'taxpayments') {
            endpoint = "taxpayments"
        } else if (parseData.sType === 'casino') {
            endpoint = "casinorev"
        }
        const fetchUrl = `${UTILS}?action=${endpoint}`;

        try {
            const response = await fetch(fetchUrl, {
                method: "POST",
                headers: {
                    'Content-Type': 'text/plain'
                },
                body: JSON.stringify(requestData)
            });



            // Handle potential BOM and control characters in response
            const text = await response.text();
            const cleanedText = text.replace(/^[\uFEFF\x00-\x1F\x7F-\x9F]+|[\uFEFF\x00-\x1F\x7F-\x9F]+$/g, "");
            let data;
            try {
                data = JSON.parse(cleanedText);
            } catch (parseError) {
                console.error("Failed to parse response:", {
                    error: parseError,
                    responseText: text,
                    cleanedText: cleanedText
                });
                throw new Error("Invalid server response format");
            }

            console.log("Server response:", data);

            if (data.status === "success") {
                // Capture data BEFORE resetting
                setSubmittedData({
                    formData: { ...formData },
                    sectionGroups: [...sectionGroups],
                    taxPayerName
                });

                setShowDownloadPopup(true);

                // Now reset form data
                const resetFormData = {};
                formSections.taxrevenue.fields.forEach(field => {
                    resetFormData[field.fieldname] = getSQLDefaultValue(field.defaultvalue) || "";
                });

                // Reset section groups
                setSectionGroups([{
                    id: 1,
                    exemptionGroups: [1],
                    splitpayment: sectionGroups[0].splitpayment,
                    exemption: sectionGroups[0].exemption
                }]);

                if (exemptionCodeFieldName) {
                    resetFormData[`${exemptionCodeFieldName}_1_1`] = "";
                }
                if (exemptionAmountFieldName) {
                    resetFormData[`${exemptionAmountFieldName}_1_1`] = "";
                }
                if (exemptionTaxTypeFieldName) {
                    resetFormData[`${exemptionTaxTypeFieldName}_1_1`] = "";
                }

                setFormData(resetFormData);
                if (sigPad.current) sigPad.current.clear();
                setFiles([{ id: 1, file: null, type: "" }]);
                setDate("");
                setIsSigned(false);
            } else {
                throw new Error(data.message || "Submission failed");
            }
        } catch (error) {
            console.error("Submission error:", {
                error: error,
                message: error.message,
                stack: error.stack
            });
            setError(error.message || "An unknown error occurred");
        } finally {
            setIsAPILoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setShowConfirmation(true);
    };

    const clearExemptionCodesForGroup = (groupId) => {
        setFormData(prev => {
            const newData = { ...prev };

            // Find all exemption code fields for this group and clear them
            Object.keys(prev).forEach(key => {
                if (key.startsWith(`${exemptionCodeFieldName}_${groupId}_`)) {
                    newData[key] = "";
                }

                // Optionally clear exemption amounts too
                if (key.startsWith(`${exemptionAmountFieldName}_${groupId}_`)) {
                    newData[key] = "";
                }
            });

            return newData;
        });

        // Also clear the exemption code options cache for this group
        setExemptionCodeOptions(prev => {
            const newOptions = { ...prev };
            Object.keys(newOptions).forEach(key => {
                if (key.startsWith(`${groupId}_`)) {
                    delete newOptions[key];
                }
            });
            return newOptions;
        });
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name.includes(splitPaymentFieldNames['Payment for'])) {
            // Extract group ID from the field name
            const groupId = name.split('_').pop();

            // Clear all exemption codes for this group
            clearExemptionCodesForGroup(groupId);
        }

        if (name === 'frc') {
            setInputValue(value);

            if (value.length >= 3) {
                fetchZipData(value);
                setShowZipDropdown(true);
            } else {
                setShowZipDropdown(false);
                setFormData((prevFormData) => ({ ...prevFormData, frc: "" }));
            }
        } else if (type === 'checkbox') {
            setFormData((prevFormData) => {
                const currentValues = prevFormData[name] || [];
                let updatedValues;

                if (checked) {
                    updatedValues = [...currentValues, value];
                } else {
                    updatedValues = currentValues.filter((item) => item !== value);
                }

                return {
                    ...prevFormData,
                    [name]: updatedValues,
                };
            });
        } else {
            setFormData((prevFormData) => ({
                ...prevFormData,
                [name]: value,
            }));
        }
    };

    const fetchZipData = async (zip) => {
        try {
            const response = await fetch(UTILS, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "Accept": "application/json"
                },
                body: new URLSearchParams({ action: "zip", s: zip }),
            });
            const text = await response.text();
            const cleanedText = text.replace(/[\x00-\x1F\x7F-\x9F]/g, "");

            let data;
            try {
                data = JSON.parse(cleanedText);
            } catch (parseError) {
                console.error("Failed to parse ZIP data:", parseError);
                throw new Error("Invalid ZIP data format");
            }

            if (response.ok) {
                setZipCodes(data);
            } else {
                throw new Error(data.message || "Failed to fetch ZIP codes");
            }
        } catch (error) {
            console.error("ZIP lookup failed:", error);
            setZipCodes([]);
        }
    };

    const handleZipSelection = (zip) => {
        setInputValue(zip.zipcode);
        setFormData((prevFormData) => ({ ...prevFormData, frc: zip.itemid }));
        setShowZipDropdown(false);
    };

    const formatPhoneOnChange = (e) => {
        const input = e.target;
        let phoneNumber = input.value.replace(/\D/g, '');
        if (phoneNumber.length > 10) {
            phoneNumber = phoneNumber.slice(0, 10);
        }

        let formattedPhone = phoneNumber;
        if (phoneNumber.length > 6) {
            formattedPhone = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
        } else if (phoneNumber.length > 3) {
            formattedPhone = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
        } else if (phoneNumber.length > 0) {
            formattedPhone = `(${phoneNumber}`;
        }

        input.value = formattedPhone;
        setFormData((prevFormData) => ({ ...prevFormData, [input.name]: formattedPhone }));
    };

    const formatSSNOnChange = (e) => {
        const input = e.target.value.replace(/\D/g, "");
        let formattedValue = "";

        if (input.length > 0) {
            formattedValue += input.substring(0, 3);
        }
        if (input.length > 3) {
            formattedValue += "-" + input.substring(3, 5);
        }
        if (input.length > 5) {
            formattedValue += "-" + input.substring(5, 9);
        }

        setFormData({ ...formData, [e.target.name]: formattedValue });
    };

    const handleCheckboxGroupChange = (e, fieldName) => {
        const { value, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [fieldName]: checked
                ? [...(prev[fieldName] || []), value]
                : (prev[fieldName] || []).filter(item => item !== value)
        }));
    };

    const handleClear = () => {
        if (sigPad.current) {
            sigPad.current.clear();
            setIsSigned(false);
        }
    };

    const handleEnd = () => {
        if (sigPad.current) {
            setIsSigned(!sigPad.current.isEmpty());
        }
    };

    const handleFileTypeChange = (index, value) => {
        const updatedFiles = [...files];
        updatedFiles[index].type = value;
        setFiles(updatedFiles);
    };

    const handleAddMore = () => {
        setFiles([...files, { id: files.length + 1, file: null, type: "" }]);
    };

    const handleFileChange = (index, event) => {
        const updatedFiles = [...files];
        updatedFiles[index].file = event.target.files[0];
        setFiles(updatedFiles);
    };

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center h-64 space-y-4">
                <div className="relative">
                    <div className="w-16 h-16 rounded-full absolute border-4 border-gray-200"></div>
                    <div className="w-16 h-16 rounded-full animate-spin border-4 border-primary border-t-transparent border-r-transparent"></div>

                    <div className="absolute inset-0 flex items-center justify-center">
                        <svg
                            className="w-6 h-6 text-primary"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 14l-7 7m0 0l-7-7m7 7V3"
                            ></path>
                        </svg>
                    </div>
                </div>

                <div className="flex flex-col items-center space-y-2">
                    <p className="text-gray-600 font-medium text-lg animate-pulse">Preparing Your Form...</p>
                    <div className="flex space-x-2">
                        {[...Array(3)].map((_, i) => (
                            <div
                                key={i}
                                className="w-2 h-2 bg-primary rounded-full animate-bounce"
                                style={{ animationDelay: `${i * 0.1}s` }}
                            ></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Form</h2>
                <p className="text-gray-700 mb-4">{error}</p>
                <button
                    onClick={() => window.location.reload()}
                    className="bg-[#711515] text-white px-6 py-2 rounded-lg hover:bg-[#5a1010] transition"
                >
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <div className="w-[95%] md:w-[80%] p-4 md:p-8 mx-auto bg-white mt-5 rounded-lg shadow-lg">
            {error && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
                    <p>{error}</p>
                </div>
            )}

            <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center">{parseData?.activeComponent} Form</h1>

            {taxPayerName && (
                <p className="text-lg text-gray-600 -mt-6 text-center font-semibold">
                    For: <span className="text-lg  text-[#711515]">{taxPayerName}</span>
                </p>
            )}

            {/* Tax Rates Table */}


            <form onSubmit={handleSubmit} className="flex flex-col gap-8">
                {/* Tax Revenue Information (always shown once) */}
                <div key="taxrevenue" className="flex flex-col gap-6">
                    <h3 className="text-xl font-bold text-gray-800 border-b-2 border-[#711515] pb-2">
                        {formSections.taxrevenue.title}
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {formSections.taxrevenue.fields
                            .filter(
                                (field) =>
                                    ![
                                        "itemid",
                                        "autonumber",
                                        "readonlydate",
                                        "readonlypicklist",
                                        "readonly",
                                        "readonlynumeric",
                                        "hidden",
                                        "readonlylookup",
                                        "linkedfield",
                                        "inlinequery",
                                        "person",
                                    ].includes(field.fieldtype)
                            )
                            .sort((a, b) => a.sortorder - b.sortorder)
                            .map((field) => (
                                <div className={`${field.fieldtype === "label" ? "col-span-2" : "col-span-1"}`} key={field.displayid}>
                                    <label className={`block ${field.fieldtype === "label" ? "font-bold text-base text-xl w-full" : "text-gray-700 font-semibold"} mb-3`}>
                                        {field.required === "Y" && <span className="text-[#711515]">*</span>} {field.displayname}
                                        {field.tooltip && (
                                            <div className="inline-block relative ml-2 group">
                                                <svg
                                                    className="w-5 h-5 text-gray-400 cursor-help"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth="2"
                                                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                    />
                                                </svg>
                                                <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 w-64 p-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                                                    {field.tooltip}
                                                    <div className="absolute left-1/2 transform -translate-x-1/2 top-full w-2 h-2 bg-gray-800 rotate-45"></div>
                                                </div>
                                            </div>
                                        )}
                                    </label>

                                    {field.fieldtype === "lookup" ? (
                                        <div className="relative">
                                            <input
                                                type="text"
                                                name={field.fieldname}
                                                value={inputValue}
                                                onChange={handleChange}
                                                className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                                style={{
                                                    width: '100%'
                                                }}
                                                required={field.required === "Y"}
                                                maxLength={field.maxlength || ""}
                                                autoComplete="off"
                                                ref={inputRef}
                                            />
                                            {showZipDropdown && field.fieldname === 'frc' && (
                                                <div
                                                    className="absolute z-10 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                                                    style={{ width: inputRef.current ? inputRef.current.offsetWidth : '100%' }}
                                                >
                                                    {zipCodes.length > 0 ? (
                                                        zipCodes.map((zip) => (
                                                            <div
                                                                key={zip.itemid}
                                                                className="p-3 hover:bg-gray-100 cursor-pointer"
                                                                onClick={() => handleZipSelection(zip)}
                                                            >
                                                                {zip.zipcode}
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="p-3 text-gray-500">No results found</div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ) : field.fieldtype === "date" ? (
                                        <input
                                            type="date"
                                            name={field.fieldname}
                                            value={formData[field.fieldname] || ""}
                                            onChange={handleChange}
                                            className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                            required={field.required === "Y"}
                                        />
                                    ) : field.fieldtype === "text" || field.fieldtype === "numeric" || field.fieldtype === "currency" || field.fieldtype === "textdate" ? (
                                        <>
                                            <input
                                                type="text"
                                                name={field.fieldname}
                                                value={formData[field.fieldname] || ""}
                                                onChange={
                                                    field.onblur === "formatPhone(this);"
                                                        ? formatPhoneOnChange
                                                        : field.onfocus === "formatSSN(this);"
                                                            ? formatSSNOnChange
                                                            : handleChange
                                                }
                                                className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                                style={{
                                                    width: '100%'
                                                }}
                                                required={field.required === "Y"}
                                                maxLength={field.maxlength || ""}
                                            />
                                            {field.displayname === "Email Address" && emailError && (
                                                <p className="text-[#711515] text-sm mt-2">{emailError}</p>
                                            )}
                                        </>
                                    ) : field.fieldtype === "textarea" ? (
                                        <textarea
                                            name={field.fieldname}
                                            value={formData[field.fieldname] || ""}
                                            onChange={handleChange}
                                            className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                            required={field.required === "Y"}
                                            rows="4"
                                        ></textarea>
                                    ) : field.fieldtype === "picklist" ? (
                                        <div className="flex flex-col w-full">
                                            <div className="relative w-full">
                                                <select
                                                    name={field.fieldname}
                                                    value={formData[field.fieldname] || ""}
                                                    onChange={handleChange}
                                                    className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent appearance-none"
                                                    required={field.required === "Y"}
                                                >
                                                    <option value="">Select {field.displayname}</option>
                                                    {picklistCache[field.itemvaluegroup]?.isLoading ? (
                                                        <option disabled>Loading options...</option>
                                                    ) : (
                                                        (picklistCache[field.itemvaluegroup]?.data || []).map((option) => (
                                                            <option key={option.valueid} value={option.valuetext}>
                                                                {option.valuetext}
                                                            </option>
                                                        ))
                                                    )}
                                                </select>
                                                <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-700">
                                                    <svg
                                                        className="h-4 w-4"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 20 20"
                                                        fill="currentColor"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                </div>
                                            </div>
                                            {picklistCache[field.itemvaluegroup]?.error && (
                                                <p className="text-red-500 text-sm mt-1">
                                                    {picklistCache[field.itemvaluegroup].error}
                                                </p>
                                            )}
                                        </div>
                                    ) : field.fieldtype === "datetimepl" ? (
                                        <div className="w-full">
                                            <DatePicker
                                                selected={formData[field.fieldname] ? new Date(formData[field.fieldname]) : null}
                                                onChange={(date) =>
                                                    setFormData({
                                                        ...formData,
                                                        [field.fieldname]: date.toISOString()
                                                    })
                                                }
                                                showTimeSelect
                                                timeFormat="HH:mm"
                                                timeIntervals={15}
                                                dateFormat="MMMM d, yyyy h:mm aa"
                                                className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                                required={field.required === "Y"}
                                                placeholderText="Select date and time"
                                            />
                                        </div>
                                    ) : field.fieldtype === "date" ? (
                                        <input
                                            type="date"
                                            name={field.fieldname}
                                            value={formData[field.fieldname] || ""}
                                            onChange={handleChange}
                                            className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                            required={field.required === "Y"}
                                        />
                                    ) : field.fieldtype === "checkbox" ? (
                                        <CheckboxField
                                            field={field}
                                            formData={formData}
                                            handleCheckboxGroupChange={handleCheckboxGroupChange}
                                            picklistCache={picklistCache}
                                            setPicklistCache={setPicklistCache}
                                        />
                                    ) : field.fieldtype === "radio" ? (
                                        <RadioField
                                            field={field}
                                            formData={formData}
                                            handleChange={handleChange}
                                            picklistCache={picklistCache}
                                            setPicklistCache={setPicklistCache}
                                        />
                                    ) : null}
                                </div>
                            ))}
                    </div>
                </div>

                {/* Section Groups (Split Payment + Exemption) */}
                {sectionGroups.map((group) => (
                    <div key={group.id} className="border border-gray-200 rounded-lg p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold text-gray-800">Line {group.id}</h3>
                            {group.id > 1 && (
                                <button
                                    type="button"
                                    onClick={() => handleRemoveSectionGroup(group.id)}
                                    className="text-red-500 hover:text-red-700 font-medium"
                                >
                                    Remove Line
                                </button>
                            )}
                        </div>

                        {/* Split Payment Information */}
                        <div className="mb-8">
                            <h4 className="text-lg font-bold text-gray-700 border-b border-gray-300 pb-2 mb-4">
                                {group.splitpayment.title}
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {group.splitpayment.fields
                                    .filter(
                                        (field) =>
                                            ![
                                                "itemid",
                                                "autonumber",
                                                "readonlydate",
                                                "readonlypicklist",
                                                "readonlynumeric",
                                                "readonly",
                                                "hidden",
                                                "readonlylookup",
                                                "linkedfield",
                                                "inlinequery",
                                                "person",
                                            ].includes(field.fieldtype)
                                    )
                                    .sort((a, b) => a.sortorder - b.sortorder)
                                    .map((field) => (
                                        <div className={`${field.fieldtype === "label" ? "col-span-2" : "col-span-1"}`} key={`split_${group.id}_${field.displayid}`}>
                                            <label className={`block ${field.fieldtype === "label" ? "font-bold text-base text-xl w-full" : "text-gray-700 font-semibold"} mb-3`}>
                                                {field.required === "Y" && <span className="text-[#711515]">*</span>} {field.displayname}
                                                {field.tooltip && (
                                                    <div className="inline-block relative ml-2 group">
                                                        <svg
                                                            className="w-5 h-5 text-gray-400 cursor-help"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth="2"
                                                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                            />
                                                        </svg>
                                                        <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 w-64 p-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                                                            {field.tooltip}
                                                            <div className="absolute left-1/2 transform -translate-x-1/2 top-full w-2 h-2 bg-gray-800 rotate-45"></div>
                                                        </div>
                                                    </div>
                                                )}
                                            </label>

                                            {field.displayname === "Payment for" ? (
                                                <div className="relative">
                                                    <select
                                                        name={`${field.fieldname}_${group.id}`}
                                                        value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                        onChange={handleChange}
                                                        className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent appearance-none"
                                                        required={field.required === "Y"}
                                                    >
                                                        <option value="">Select Payment for</option>
                                                        {taxTypeOptions.map((option, index) => (
                                                            <option key={index} value={option.sval}>
                                                                {option.dval}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-700">
                                                        <svg
                                                            className="h-4 w-4"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 20 20"
                                                            fill="currentColor"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                    </div>
                                                </div>
                                            ) : field.fieldtype === "date" ? (
                                                <input
                                                    type="date"
                                                    name={`${field.fieldname}_${group.id}`}
                                                    value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                    onChange={handleChange}
                                                    className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                                    required={field.required === "Y"}
                                                />
                                            ) : field.displayname === "Gross Amount" ? (
                                                <input
                                                    type="number"
                                                    name={`${field.fieldname}_${group.id}`}
                                                    value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                    onChange={handleChange}
                                                    className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                                    required={field.required === "Y"}
                                                    step="0.01"
                                                    min="0"
                                                />
                                            ) : field.displayname === "Reported Exempt Sales" ? (
                                                <input
                                                    type="number"
                                                    name={`${field.fieldname}_${group.id}`}
                                                    value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                    onChange={handleChange}
                                                    className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                                    required={field.required === "Y"}
                                                    step="0.01"
                                                    min="0"
                                                />
                                            ) : field.displayname === "Actual Taxable Sales" ? (
                                                <input
                                                    type="number"
                                                    name={`${field.fieldname}_${group.id}`}
                                                    value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                    className="w-full p-4 border-2 border-gray-200 rounded-lg bg-gray-100"
                                                    readOnly
                                                    step="0.01"
                                                />
                                            ) : field.displayname === "Actual Tax Amount Due" ? (
                                                <input
                                                    type="number"
                                                    name={`${field.fieldname}_${group.id}`}
                                                    value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                    className="w-full p-4 border-2 border-gray-200 rounded-lg bg-gray-100"
                                                    readOnly
                                                    step="0.01"
                                                />
                                            ) : field.fieldtype === "date" ? (
                                                <input
                                                    type="date"
                                                    name={`${field.fieldname}_${group.id}`}
                                                    value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                    onChange={handleChange}
                                                    className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                                    required={field.required === "Y"}
                                                />
                                            ) : field.fieldtype === "text" || field.fieldtype === "numeric" || field.fieldtype === "currency" || field.fieldtype === "textdate" ? (
                                                <input
                                                    type="text"
                                                    name={`${field.fieldname}_${group.id}`}
                                                    value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                    onChange={handleChange}
                                                    className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                                    required={field.required === "Y"}
                                                    maxLength={field.maxlength || ""}
                                                />
                                            ) : field.fieldtype === "textarea" ? (
                                                <textarea
                                                    name={`${field.fieldname}_${group.id}`}
                                                    value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                    onChange={handleChange}
                                                    className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                                    required={field.required === "Y"}
                                                    rows="4"
                                                ></textarea>
                                            ) : field.fieldtype === "picklist" ? (
                                                <div className="relative">
                                                    <select
                                                        name={`${field.fieldname}_${group.id}`}
                                                        value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                        onChange={handleChange}
                                                        className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent appearance-none"
                                                        required={field.required === "Y"}
                                                    >
                                                        <option value="">Select {field.displayname}</option>
                                                        {picklistCache[field.itemvaluegroup]?.data?.map((option) => (
                                                            <option key={option.valueid} value={option.valuetext}>
                                                                {option.valuetext}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-700">
                                                        <svg
                                                            className="h-4 w-4"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 20 20"
                                                            fill="currentColor"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                    </div>
                                                </div>
                                            ) : field.fieldtype === "checkbox" ? (
                                                <CheckboxField
                                                    field={field}
                                                    formData={formData}
                                                    handleCheckboxGroupChange={handleCheckboxGroupChange}
                                                    picklistCache={picklistCache}
                                                    setPicklistCache={setPicklistCache}
                                                    groupId={group.id}
                                                />
                                            ) : field.fieldtype === "radio" ? (
                                                <RadioField
                                                    field={field}
                                                    formData={formData}
                                                    handleChange={handleChange}
                                                    picklistCache={picklistCache}
                                                    setPicklistCache={setPicklistCache}
                                                    groupId={group.id}
                                                />
                                            ) : null}
                                        </div>
                                    ))}
                            </div>
                        </div>

                        {/* Exemption Information */}
                        <div>
                            <h4 className="text-lg font-bold text-gray-700 border-b border-gray-300 pb-2 mb-4">
                                {group.exemption.title}
                            </h4>
                            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded mb-4">
                                <p className="text-gray-700 font-medium">
                                    Instructions: The total of exemptions/exclusions must match the amount in the Reported Exempt Sales field above.
                                </p>
                            </div>

                            {group.exemptionGroups.map(exemptionId => (
                                <div key={`exemption_${group.id}_${exemptionId}`} className="border border-gray-200 rounded-lg p-6 mb-6">
                                    <div className="flex justify-between items-center mb-4">
                                        <h5 className="text-md font-bold text-gray-700">
                                            {parseData?.sType === 'packsInv'
                                                ? `Packs Distributed ${exemptionId}`
                                                : parseData?.sType === 'casino'
                                                    ? `Outlets ${exemptionId}`
                                                    : `Exemption ${exemptionId}`}
                                        </h5>
                                        {group.exemptionGroups.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveExemptionGroup(group.id, exemptionId)}
                                                className="text-red-500 hover:text-red-700 font-medium"
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {group.exemption.fields
                                            .filter(
                                                (field) =>
                                                    ![
                                                        "itemid",
                                                        "autonumber",
                                                        "readonlydate",
                                                        "readonlypicklist",
                                                        "readonly",
                                                        "hidden",
                                                        "readonlylookup",
                                                        "linkedfield",
                                                        "inlinequery",
                                                        "person",
                                                    ].includes(field.fieldtype)
                                            )
                                            .sort((a, b) => a.sortorder - b.sortorder)
                                            .map((field) => (
                                                <div className={`${field.fieldtype === "label" ? "col-span-2" : "col-span-1"}`} key={`${group.id}_${exemptionId}_${field.displayid}`}>
                                                    <label className={`block ${field.fieldtype === "label" ? "font-bold text-base text-xl w-full" : "text-gray-700 font-semibold"} mb-3`}>
                                                        {field.required === "Y" && <span className="text-[#711515]">*</span>} {field.displayname}
                                                        {field.tooltip && (
                                                            <div className="inline-block relative ml-2 group">
                                                                <svg
                                                                    className="w-5 h-5 text-gray-400 cursor-help"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    viewBox="0 0 24 24"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        strokeWidth="2"
                                                                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                                    />
                                                                </svg>
                                                                <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 w-64 p-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                                                                    {field.tooltip}
                                                                    <div className="absolute left-1/2 transform -translate-x-1/2 top-full w-2 h-2 bg-gray-800 rotate-45"></div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </label>

                                                    {field.fieldname === exemptionTaxTypeFieldName ? (
                                                        <div className="relative">
                                                            <select
                                                                name={`${field.fieldname}_${group.id}_${exemptionId}`}
                                                                value={formData[`${field.fieldname}_${group.id}_${exemptionId}`] || ""}
                                                                onChange={handleChange}
                                                                className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent appearance-none"
                                                                required={field.required === "Y"}
                                                            >
                                                                <option value="">Select Payment for</option>
                                                                {taxTypeOptions.map((option, index) => (
                                                                    <option key={index} value={option.sval}>
                                                                        {option.dval}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-700">
                                                                <svg
                                                                    className="h-4 w-4"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    viewBox="0 0 20 20"
                                                                    fill="currentColor"
                                                                >
                                                                    <path
                                                                        fillRule="evenodd"
                                                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                                        clipRule="evenodd"
                                                                    />
                                                                </svg>
                                                            </div>
                                                        </div>
                                                    ) : field.displayname === "Retailer" ? (
                                                        <div className="relative">
                                                            <select
                                                                name={`${field.fieldname}_${group.id}_${exemptionId}`}
                                                                value={formData[`${field.fieldname}_${group.id}_${exemptionId}`] || ""}
                                                                onChange={handleChange}
                                                                className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent appearance-none"
                                                                required={field.required === "Y"}
                                                            >
                                                                <option value="">Select Retailer</option>
                                                                {retailersOptions.map((option, index) => (
                                                                    <option key={index} value={option.sval}>{option.dval}</option>
                                                                ))}
                                                            </select>
                                                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-700">
                                                                <svg
                                                                    className="h-4 w-4"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    viewBox="0 0 20 20"
                                                                    fill="currentColor"
                                                                >
                                                                    <path
                                                                        fillRule="evenodd"
                                                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                                        clipRule="evenodd"
                                                                    />
                                                                </svg>
                                                            </div>
                                                        </div>
                                                    ) : field.displayname === "Exemption Code" ? (
                                                        <div className="relative">
                                                            <select
                                                                name={`${field.fieldname}_${group.id}_${exemptionId}`}
                                                                value={formData[`${field.fieldname}_${group.id}_${exemptionId}`] || ""}
                                                                onChange={handleChange}
                                                                onClick={() => {
                                                                    const taxTypeKey = `${splitPaymentFieldNames['Payment for']}_${group.id}`;
                                                                    const taxType = formData[taxTypeKey] || '';
                                                                    if (taxType) {
                                                                        fetchExemptionCodes(taxType, group.id, exemptionId);
                                                                    }
                                                                }}
                                                                disabled={!formData[`${splitPaymentFieldNames['Payment for']}_${group.id}`]}
                                                                className={`w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent appearance-none ${!formData[`${splitPaymentFieldNames['Payment for']}_${group.id}`]
                                                                    ? 'bg-gray-100 cursor-not-allowed'
                                                                    : ''
                                                                    }`}
                                                                required={field.required === "Y"}
                                                            >
                                                                <option value="">
                                                                    {!formData[`${splitPaymentFieldNames['Payment for']}_${group.id}`]
                                                                        ? "Select Payment for first"
                                                                        : "Select Exemption Code"
                                                                    }
                                                                </option>
                                                                {exemptionCodeOptions[`${group.id}_${exemptionId}`]?.map((option, index) => (
                                                                    <option key={index} value={option.valuetext}>
                                                                        {option.valuetext}
                                                                    </option>
                                                                )) || []}
                                                            </select>
                                                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-700">
                                                                <svg
                                                                    className="h-4 w-4"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    viewBox="0 0 20 20"
                                                                    fill="currentColor"
                                                                >
                                                                    <path
                                                                        fillRule="evenodd"
                                                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                                        clipRule="evenodd"
                                                                    />
                                                                </svg>
                                                            </div>
                                                        </div>
                                                    ) : field.displayname === "Exemption Amount" ? (
                                                        <input
                                                            type="text"
                                                            name={`${field.fieldname}_${group.id}_${exemptionId}`}
                                                            value={formData[`${field.fieldname}_${group.id}_${exemptionId}`] || ""}
                                                            onChange={handleChange}
                                                            className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                                            required={field.required === "Y"}
                                                        />
                                                    ) : field.fieldtype === "date" ? (
                                                        <input
                                                            type="date"
                                                            name={`${field.fieldname}_${group.id}`}
                                                            value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                            onChange={handleChange}
                                                            className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                                            required={field.required === "Y"}
                                                        />
                                                    ) : field.fieldtype === "text" || field.fieldtype === "numeric" || field.fieldtype === "currency" ? (
                                                        <input
                                                            type="text"
                                                            name={`${field.fieldname}_${group.id}_${exemptionId}`}
                                                            value={formData[`${field.fieldname}_${group.id}_${exemptionId}`] || ""}
                                                            onChange={handleChange}
                                                            className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                                            required={field.required === "Y"}
                                                        />
                                                    ) : field.fieldtype === "picklist" ? (
                                                        <div className="relative">
                                                            <select
                                                                name={`${field.fieldname}_${group.id}_${exemptionId}`}
                                                                value={formData[`${field.fieldname}_${group.id}_${exemptionId}`] || ""}
                                                                onChange={handleChange}
                                                                className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent appearance-none"
                                                                required={field.required === "Y"}
                                                            >
                                                                <option value="">Select {field.displayname}</option>
                                                                {picklistCache[field.itemvaluegroup]?.data?.map((option) => (
                                                                    <option key={option.valueid} value={option.valuetext}>
                                                                        {option.valuetext}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-700">
                                                                <svg
                                                                    className="h-4 w-4"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    viewBox="0 0 20 20"
                                                                    fill="currentColor"
                                                                >
                                                                    <path
                                                                        fillRule="evenodd"
                                                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                                        clipRule="evenodd"
                                                                    />
                                                                </svg>
                                                            </div>
                                                        </div>
                                                    ) : null}
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            ))}

                            <button
                                type="button"
                                onClick={() => handleAddExemptionGroup(group.id)}
                                className="flex items-center justify-center bg-[#711515] text-white px-4 py-2 rounded-lg hover:bg-[#5a1010] transition mt-4"
                            >
                                <svg
                                    className="w-5 h-5 mr-2"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                {parseData?.sType === 'packsInv'
                                    ? 'Add more packs distributed'
                                    : parseData?.sType === 'casino'
                                        ? 'Add more outlets'
                                        : 'Add More Exemptions'}
                            </button>
                        </div>
                    </div>
                ))}

                {/* Add Section Group Button */}
                <button
                    type="button"
                    onClick={handleAddSectionGroup}
                    className="flex items-center justify-center bg-[#711515] text-white px-6 py-3 rounded-lg hover:bg-[#5a1010] transition"
                >
                    <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Another Line
                </button>

                <div className="col-span-2 flex justify-center mt-6">
                    <button
                        type="submit"
                        disabled={isAPILoading || (showSignature && !isSigned)}
                        className={`bg-[#711515] text-white px-6 py-3 rounded-lg ${isAPILoading || (showSignature && !isSigned) ? "opacity-50 cursor-not-allowed" : "hover:bg-[#5a1010]"} transition`}
                    >
                        {isAPILoading ? "Submitting..." : "Submit"}
                    </button>
                </div>
            </form>

            {/* Confirmation Modal */}
            <ConfirmationModal
                open={showConfirmation}
                onClose={() => setShowConfirmation(false)}
                onConfirm={confirmSubmit}
                formSections={formSections}
                sectionGroups={sectionGroups}
                formData={formData}
                title={parseData?.activeComponent}
                exmptitle={
                    parseData?.sType === 'packsInv'
                        ? 'Packs Distributed'
                        : parseData?.sType === 'casino'
                            ? 'Outlets'
                            : 'Exemptions'
                }
            />

            {/* Download Popup */}
            {showDownloadPopup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                        <h2 className="text-2xl font-bold mb-4">Form Submitted Successfully</h2>
                        <p className="mb-6">Your form has been received. Would you like to download a PDF copy?</p>
                        <div className="flex justify-center space-x-4">
                            <button
                                onClick={() => setShowDownloadPopup(false)}
                                className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition"
                            >
                                Close
                            </button>
                            <button
                                onClick={() => {
                                    if (submittedData) {
                                        generatePDF(submittedData.formData, submittedData.sectionGroups, submittedData.taxPayerName);
                                    }
                                    setShowDownloadPopup(false);
                                }}
                                className="bg-[#711515] text-white px-4 py-2 rounded-lg hover:bg-[#5a1010] transition"
                            >
                                Download PDF
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ThreeTaxReturn;
